import { Button, Typography } from "matsuri-ui"
import { Fragment, useMemo } from "react"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import AddRoundedIcon from "@mui/icons-material/AddRounded"
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded"
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded"
import {
    CategoryBar,
    EmptyState,
    InstructionBanner,
    ItemList,
    ItemName,
    ItemRow,
    Main,
    PaddedArea,
    PropertyBar,
    QuantityValue,
    Stepper,
    StepperButton,
    TitleAccent
} from "./styled"
import { MobileLayout, displayName, groupByCategory } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import { RoomSupplyItem } from "@/hooks/useRoomSupply"
import { useTranslation } from "@/hooks/useTranslation"

// 1品目あたりの上限。誤タップで極端な数量にならないよう抑える。
export const MaxQuantity = 20

interface Props {
    items: RoomSupplyItem[]
    quantities: Record<string, number>
    selectedCount: number
    listingName?: string
    layout: MobileLayout
    onQuantityChange: (itemId: string, diff: number) => void
    onConfirm: () => void
    onSkip: () => void
    onClose: () => void
}

export const SelectItemsView = ({
    items,
    quantities,
    selectedCount,
    listingName,
    layout,
    onQuantityChange,
    onConfirm,
    onSkip,
    onClose
}: Props) => {
    const { t } = useTranslation()
    const groups = useMemo(() => groupByCategory(items), [items])

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">
                            {t("備品の不足確認")}
                        </Typography>
                    }
                    onNext={onClose}
                    nextIcon={<CloseIcon />}
                />
            </Header>

            <div {...layout.bodyProps}>
                {listingName && (
                    <PropertyBar>
                        <ApartmentRoundedIcon style={{ fontSize: 18 }} />
                        {listingName}
                    </PropertyBar>
                )}

                <PaddedArea>
                    <InstructionBanner>
                        <AssignmentRoundedIcon style={{ fontSize: 20 }} />
                        <span>
                            {t(
                                "退室時に備品の状況を確認し、不足分をご入力ください。"
                            )}
                        </span>
                    </InstructionBanner>
                </PaddedArea>

                {groups.length === 0 && (
                    <PaddedArea>
                        <EmptyState>
                            {t("報告できる品目が登録されていません")}
                        </EmptyState>
                    </PaddedArea>
                )}

                <ItemList>
                    {groups.map(group => (
                        <Fragment key={group.category}>
                            <CategoryBar>
                                <TitleAccent />
                                {group.category}
                            </CategoryBar>
                            {group.items.map(item => {
                                const quantity = quantities[item.id] ?? 0
                                const name = displayName(item)
                                return (
                                    <ItemRow key={item.id}>
                                        <ItemName selected={quantity > 0}>
                                            {name}
                                        </ItemName>
                                        <Stepper>
                                            <StepperButton
                                                type="button"
                                                aria-label={t(
                                                    "{{name}}を減らす",
                                                    {
                                                        params: { name }
                                                    }
                                                )}
                                                disabled={quantity === 0}
                                                onClick={() =>
                                                    onQuantityChange(
                                                        item.id,
                                                        -1
                                                    )
                                                }
                                            >
                                                <RemoveRoundedIcon
                                                    style={{ fontSize: 18 }}
                                                />
                                            </StepperButton>
                                            <QuantityValue
                                                selected={quantity > 0}
                                            >
                                                {quantity}
                                            </QuantityValue>
                                            <StepperButton
                                                type="button"
                                                aria-label={t(
                                                    "{{name}}を増やす",
                                                    {
                                                        params: { name }
                                                    }
                                                )}
                                                disabled={
                                                    quantity >= MaxQuantity
                                                }
                                                onClick={() =>
                                                    onQuantityChange(item.id, 1)
                                                }
                                            >
                                                <AddRoundedIcon
                                                    style={{ fontSize: 18 }}
                                                />
                                            </StepperButton>
                                        </Stepper>
                                    </ItemRow>
                                )
                            })}
                        </Fragment>
                    ))}
                </ItemList>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color={selectedCount > 0 ? "primary" : undefined}
                    fullWidth
                    disabled={selectedCount === 0}
                    onClick={onConfirm}
                >
                    {selectedCount > 0
                        ? t("報告する（{{count}}品目）", {
                              params: { count: selectedCount }
                          })
                        : t("報告する（品目を選択してください）")}
                </Button>
                {/*
                    報告 API は items が空だと 400 を返すため、送信せず次へ進めている。
                    TODO: 「確認したが不足は無かった」を記録として残す必要があるか要確認。
                    必要なら API に空配列を許容してもらい、このボタンでも報告を送る。
                */}
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onSkip}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {t("不足はありません")}
                </Button>
            </ReportFooter>
        </Main>
    )
}
